/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import Carousel from '@/components/Carousel';
import type {
  ContentStackSuperDuperHero,
  ContentStackHorizontalScrollCards,
} from '@/features/contentstack/interface';
import { useMemo, useState } from 'react';
import type { CarouselItem } from '../../interface';
import { CarouselImage } from '../HomepageHero/CarouselImage';
import { useCitrusHomepageData } from '@/features/citrus/api/getCitrusHomepageData';
import { reportCitrusImpression } from '@/features/citrus/utils/reportCitrusImpression';
import { mapCitrusBannerToContentStackBanner } from '@/features/citrus/utils/mapCitrusBannerToContentStackBanner';
import styles from './SuperDuperHero.module.scss';
import cx from 'classnames';
import CarouselTopSection from './CarouselTopSection';
import HorizontalCards from './HorizontalCards';
import { ariaLabelForCurrentSlide, mergeBanners } from './SuperDuperHelpers';
import { isCitrusBannerWithLink } from '@/features/citrus/utils/isCitrusBannerWithLink';
import { useFeatureFlag } from '@/features/globalConfig';
import { useLabels } from '@/hooks/useLabels';

const DEFAULT_ROTATION_SPEED_SECS = 5;

export type SuperDuperHeroProps = {
  options: ContentStackSuperDuperHero;
  horizontalCards?: ContentStackHorizontalScrollCards['card_content'];
};

const labelMap = {
  clickForDetails: 'label_click_for_details',
} as const;

export const SuperDuperHero = ({ options, horizontalCards }: SuperDuperHeroProps) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slideToImage, setSlideToImage] = useState('');
  const [autoRotationEnabled, setAutoRotationEnabled] = useState(true);
  const citrusHeroEnabled = useFeatureFlag('IS_CITRUS_HERO_ENABLED') === 'true';
  const labels = useLabels(labelMap);

  const { data: citrusData } = useCitrusHomepageData({
    enabled: citrusHeroEnabled,
  });

  const cmsCarouselBanners = useMemo(() => {
    const modularBlocks = options?.carousel_content_1 ?? [];

    return modularBlocks
      .map((block) => {
        if (block.cs_carousel_content) {
          const { image, link, legal, legal_link_color, segmentation } = block.cs_carousel_content;

          return {
            image: {
              image_url: image?.image_url,
              image_alt_text: image?.image_alt_text,
              attributes: image?.attributes || [],
            },
            link,
            legal,
            legal_link_color,
            segmentation,
          };
        }
        const placeholderKey = Object.keys(block).find((key) =>
          key.startsWith('azmn_placeholder_'),
        );
        if (placeholderKey) {
          const number = +placeholderKey.slice(17);
          return !isNaN(number) ? { placeholder_value: number } : {};
        }

        return;
      })

      .filter(Boolean) as CarouselItem[];
  }, [options?.carousel_content_1]);

  const [carouselItems, carouselItemCount]: [CarouselItem[], number] = useMemo(() => {
    const carouselBanners =
      citrusData?.ads?.find((item) => item.slotId === 'carousel-banner')?.banners ?? [];

    const citrusCarouselBanners = carouselBanners
      .map((item) => {
        if (!isCitrusBannerWithLink(item)) {
          return null;
        }

        const cmsBanner = mapCitrusBannerToContentStackBanner(item, labels.clickForDetails);
        return {
          image: cmsBanner.image,
          link: cmsBanner.link,
          legal: cmsBanner.legal,
          citrusInfo: {
            citrusAdId: item.citrusAdId,
            position: item.position,
          },
        };
      })
      .filter(Boolean) as CarouselItem[];

    const mergedBanners = mergeBanners(cmsCarouselBanners, citrusCarouselBanners);

    return [mergedBanners, mergedBanners.length];
  }, [citrusData?.ads, cmsCarouselBanners, labels.clickForDetails]);

  if (carouselItemCount === 0) {
    return null;
  }

  const handleImageChange = (index: number) => {
    if (index !== currentSlide) {
      setCurrentSlide(index);
      setSlideToImage('');
    }

    const currentItem = carouselItems[index];
    if ('citrusInfo' in currentItem && currentItem.citrusInfo?.citrusAdId) {
      void reportCitrusImpression(currentItem.citrusInfo.citrusAdId);
    }
  };

  const currentCarouselItem = carouselItems[currentSlide];
  const imageAltText = currentCarouselItem.image?.image_alt_text;
  const legalLinkName =
    currentCarouselItem.legal && 'legalLinkName' in currentCarouselItem.legal
      ? currentCarouselItem.legal?.legalLinkName
      : currentCarouselItem.legal?.link_name;
  const csLegalAriaLabel =
    currentCarouselItem.legal && 'aria_label' in currentCarouselItem.legal
      ? currentCarouselItem.legal?.aria_label
      : undefined;
  const legalLinkAriaLabel = csLegalAriaLabel || `${legalLinkName} about ${imageAltText}`;
  const superDuperHeroBannerAriaLabel = ariaLabelForCurrentSlide({
    part: currentSlide + 1,
    whole: carouselItemCount,
    altText: imageAltText,
  });

  return (
    <div
      className={cx(styles.superDuperHeroContainer, {
        [styles.heroBanner]: options.hero_banner,
      })}
      role="banner"
      aria-label={superDuperHeroBannerAriaLabel}
      tabIndex={0}
      data-testid="super-duper-hero"
    >
      <div className={styles.superDuperHero}>
        <Carousel
          autoRotationEnabled={autoRotationEnabled}
          showCarouselDots={false}
          autoRotationSpeed={(options.auto_rotation_speed ?? DEFAULT_ROTATION_SPEED_SECS) * 1000}
          classes={{
            root: cx({ [styles.carouselRoot]: !options.hero_banner }),
          }}
          onImageChange={handleImageChange}
          controlled
          selectedThumbnailIndex={currentSlide}
          slideToImage={slideToImage}
        >
          {carouselItems?.map((colItem, index) => (
            <CarouselImage
              key={colItem.image?.image_url ?? index}
              blockItem={colItem}
              itemIndex={index}
              tabindex={-1}
              isCurrentSlide={currentSlide === index}
              carouselImageClassName={
                options.hero_banner ? styles.carouselImage : styles.carouselImageXL
              }
            />
          ))}
        </Carousel>
        <CarouselTopSection
          carouselItems={carouselItems}
          carouselItemCount={carouselItemCount}
          currentSlide={currentSlide}
          autoRotationEnabled={autoRotationEnabled}
          setCurrentSlide={setSlideToImage}
          setAutoRotationEnabled={setAutoRotationEnabled}
          heroBanner={!!options.hero_banner}
          legalLinkAriaLabel={legalLinkAriaLabel}
        />
        {horizontalCards && <HorizontalCards horizontalCardsData={horizontalCards} />}
      </div>
    </div>
  );
};
